import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CatalogueApiService,
  RsApiService,
  RS_BASE_URL,
} from '@angular-workspace/services';

@Component({
  selector: 'app-gateway-data',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gateway-data.component.html',
  styleUrl: './gateway-data.component.scss',
})
export class GatewayDataComponent implements OnInit, OnDestroy {
  @Input() resourceItemData: { id?: string; name?: string; label?: string } | null = null;
  @Input() dataModel: 'model1' | 'model2' = 'model1';
  @Input() accessTypes: readonly string[] | null = null;

  @Output() closeRequested = new EventEmitter<void>();

  allData: unknown[] = [];
  displayData = '';
  isLoading = false;
  errorMessage = '';

  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;

  startDate = '';
  endDate = '';
  minDateStr = '';
  maxDateStr = '';

  private serverPageSize = 0;
  private totalEntries = 0;
  private abortController: AbortController | null = null;

  private rsApiService = inject(RsApiService);
  private catalogueApiService = inject(CatalogueApiService);
  private rsBaseUrl = inject(RS_BASE_URL);

  constructor() {
    const today = new Date();
    this.maxDateStr = this.formatDate(today);
    const minDate = new Date(today.getFullYear() - 10, 0, 1);
    this.minDateStr = this.formatDate(minDate);
  }

  async ngOnInit(): Promise<void> {
    const id = this.resourceItemData?.id;
    if (!id) {
      this.errorMessage = 'Dataset ID is not available.';
      return;
    }
    if (this.dataModel === 'model1') {
      await this.fetchEntityPage(1);
    }
  }

  ngOnDestroy(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  private updateDisplayData(): void {
    this.displayData = JSON.stringify(this.allData, null, 2);
  }

  get startIndex(): number {
    if (this.totalEntries > 0 && this.serverPageSize > 0) {
      return (this.currentPage - 1) * this.serverPageSize + 1;
    }
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get endIndex(): number {
    if (this.totalEntries > 0 && this.serverPageSize > 0) {
      return Math.min(this.currentPage * this.serverPageSize, this.totalEntries);
    }
    return Math.min(this.currentPage * this.itemsPerPage, this.allData.length);
  }

  firstPage(): void {
    if (this.currentPage === 1) return;
    if (this.dataModel === 'model1') {
      void this.fetchEntityPage(1);
    } else {
      this.currentPage = 1;
      this.updateDisplayData();
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      if (this.dataModel === 'model1') {
        void this.fetchEntityPage(this.currentPage - 1);
      } else {
        this.currentPage--;
        this.updateDisplayData();
      }
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      if (this.dataModel === 'model1') {
        void this.fetchEntityPage(this.currentPage + 1);
      } else {
        this.currentPage++;
        this.updateDisplayData();
      }
    }
  }

  lastPage(): void {
    if (this.currentPage === this.totalPages) return;
    if (this.dataModel === 'model1') {
      void this.fetchEntityPage(this.totalPages);
    } else {
      this.currentPage = this.totalPages;
      this.updateDisplayData();
    }
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    if (this.dataModel === 'model1') {
      void this.fetchEntityPage(page);
    } else {
      this.currentPage = page;
      this.updateDisplayData();
    }
  }

  getPageNumbers(): number[] {
    const maxPages = 5;
    const pages: number[] = [];
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPages - 1);
    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  }

  downloadAllData(): void {
    const dataStr = JSON.stringify(this.allData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.resourceItemData?.name || this.resourceItemData?.label || 'gateway-data'}_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  closeModal(): void {
    this.closeRequested.emit();
  }

  searchTemporalData(): void {
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select both start and end dates';
      return;
    }
    if (this.startDate > this.endDate) {
      this.errorMessage = 'Start date must be before end date';
      return;
    }
    void this.fetchTemporalData();
  }

  resetDates(): void {
    this.startDate = '';
    this.endDate = '';
    this.errorMessage = '';
    this.allData = [];
    this.displayData = '';
    this.totalPages = 0;
    this.currentPage = 1;
    this.serverPageSize = 0;
    this.totalEntries = 0;
  }

  private async fetchEntityPage(page: number): Promise<void> {
    if (this.isLoading) return;
    const id = this.resourceItemData?.id;
    if (!id) {
      this.errorMessage = 'Dataset ID is not available.';
      return;
    }
    try {
      this.isLoading = true;
      this.errorMessage = '';
      const response = await this.rsApiService.entitiesNgsiLg(id, page);
      let entries: unknown[] = [];
      if (response?.result && Array.isArray(response.result)) {
        entries = response.result;
      } else if (Array.isArray(response)) {
        entries = response;
      } else if (response?.data && Array.isArray(response.data)) {
        entries = response.data;
      } else if (response?.results && Array.isArray(response.results)) {
        entries = response.results;
      }
      this.allData = entries;
      this.currentPage = page;
      if (response?.paginationInfo) {
        this.totalPages = response.paginationInfo.totalPages || 1;
        this.serverPageSize =
          response.paginationInfo.size || entries.length || this.itemsPerPage;
        this.totalEntries = response.paginationInfo.totalCount ?? entries.length;
      } else {
        this.serverPageSize = entries.length || this.itemsPerPage;
        this.totalEntries = entries.length;
        this.totalPages = 1;
      }
      this.updateDisplayData();
    } catch {
      this.errorMessage = 'Failed to fetch entity data. Please try again.';
      this.allData = [];
      this.totalPages = 0;
      this.serverPageSize = 0;
      this.totalEntries = 0;
    } finally {
      this.isLoading = false;
    }
  }

  private async fetchTemporalData(): Promise<void> {
    if (this.isLoading) return;
    const id = this.resourceItemData?.id;
    if (!id) {
      this.errorMessage = 'Dataset ID is not available.';
      return;
    }
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const timeFrom = `${this.startDate}T00:00:00Z`;
    const timeTo = `${this.endDate}T23:59:59Z`;
    try {
      this.isLoading = true;
      this.errorMessage = '';
      const response = await this.catalogueApiService.getTemporalEntities(
        id,
        timeFrom,
        timeTo,
        this.rsBaseUrl,
        { signal }
      );
      let entries: unknown[] = [];
      if (response?.result && Array.isArray(response.result)) {
        entries = response.result;
      } else if (Array.isArray(response)) {
        entries = response;
      } else if (response?.data && Array.isArray(response.data)) {
        entries = response.data;
      } else if (response?.results && Array.isArray(response.results)) {
        entries = response.results;
      }
      this.allData = entries;
      this.currentPage = 1;
      this.totalPages = 1;
      this.serverPageSize = entries.length || this.itemsPerPage;
      this.totalEntries = entries.length;
      this.updateDisplayData();
      if (entries.length === 0) {
        this.errorMessage = 'No data found for the selected date range';
      }
    } catch (error: unknown) {
      const err = error as { name?: string };
      if (err?.name === 'AbortError') {
        return;
      }
      this.errorMessage = 'Failed to fetch temporal data. Please try again.';
      this.allData = [];
      this.totalPages = 0;
      this.serverPageSize = 0;
      this.totalEntries = 0;
    } finally {
      this.isLoading = false;
    }
  }

  private formatDate(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
